import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

function TopBenefits(): ReactElement {
    const { t } = useTranslation('top');
    const benefits = t('benefits.items', { returnObjects: true }) as Array<{
        title: string;
        before: string;
        after: string;
    }>;

    return (
        <section className="py-24 lg:py-32 bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-20 lg:mb-24">
                    <h2 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight mb-4">
                        {t('sections.benefits')}
                    </h2>
                    <div className="w-16 h-1 bg-kotomilThemaColor mx-auto rounded-full"></div>
                </div>

                <div className="space-y-12 lg:space-y-20">
                    {benefits.map((benefit, index) => (
                        <div
                            key={index}
                            className="relative flex flex-col lg:flex-row rounded-3xl shadow-2xl overflow-hidden bg-white"
                        >
                            {/* 1. BEFORE Section (Left/Top) */}
                            <div className="lg:w-[40%] bg-[#1e293b] px-8 pb-16 pt-10 lg:p-12 flex flex-col justify-center relative group shrink-0">
                                {/* Decorative Background Pattern */}
                                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

                                {/* Numbering Badge */}
                                <div className="absolute top-4 right-6 z-0">
                                    <span className="text-8xl font-black text-white/5 tracking-tighter select-none">
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                </div>

                                <div className="absolute top-8 left-8 z-10">
                                    <span className="text-xs font-black text-white tracking-widest uppercase border border-white/30 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md shadow-sm">
                                        BEFORE
                                    </span>
                                </div>

                                <div className="mt-12 relative z-10">
                                    <div className="flex items-start gap-1">
                                        <span className="text-7xl lg:text-8xl font-black text-white/50 leading-[0.8] -ml-1 select-none">
                                            {benefit.before.charAt(0)}
                                        </span>
                                        <p className="pt-2 text-xl lg:text-3xl font-bold text-white leading-relaxed drop-shadow-sm">
                                            {benefit.before.slice(1)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* 2. Arrow Connector (Centered on Seam) */}
                            <div className="relative z-30 lg:-my-10 -my-8 mx-auto lg:absolute lg:top-1/2 lg:left-[40%] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:m-0">
                                <div className="w-16 h-12 lg:w-16 lg:h-20 bg-white rounded-3xl border-4 border-gray-100 flex items-center justify-center text-gray-600">
                                    <svg
                                        className="w-10 h-10"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={3}
                                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                                            className="hidden lg:block"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={3}
                                            d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                            className="lg:hidden"
                                        />
                                    </svg>
                                </div>
                            </div>

                            {/* 3. AFTER Section (Right/Bottom) */}
                            <div className="lg:w-[60%] bg-white p-8 lg:p-12 flex flex-col justify-center relative grow">
                                <div className="absolute top-0 left-0 bg-kotomilThemaColor w-full lg:w-1.5 h-1.5 lg:h-full"></div>

                                {/* AFTER Label - Outline Style */}
                                <div className="absolute top-8 left-8 lg:left-12">
                                    <span className="flex items-center gap-2 text-sm font-black text-kotomilThemaColor tracking-widest uppercase bg-white px-5 py-2 rounded-full shadow-sm border-2 border-kotomilThemaColor transform -rotate-1">
                                        AFTER
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={3}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </span>
                                </div>

                                <div className="mt-16 mb-8 lg:pl-4">
                                    <h3 className="text-xl lg:text-2xl font-bold text-gray-900 leading-relaxed">
                                        <span dangerouslySetInnerHTML={{ __html: benefit.after }} />
                                    </h3>
                                </div>

                                {/* Change Summary - Solid Gold Impact */}
                                <div className="bg-gradient-to-br from-kotomilThemaColor to-[#facc15] rounded-xl p-6 shadow-lg flex flex-col md:flex-row md:items-center gap-4 lg:ml-4 transform hover:scale-[1.01] transition-transform duration-300">
                                    <span className="inline-block bg-white text-slate-900 font-black text-xs px-4 py-1.5 rounded-md tracking-widest uppercase shadow-sm shrink-0 self-start md:self-center">
                                        CHANGE
                                    </span>
                                    <p
                                        className="text-lg lg:text-xl font-bold text-slate-900 leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: benefit.title }}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default TopBenefits;
